import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';
import { ADMIN_PROFILE_LIST_MAX_HEIGHT } from '~src/userManagement/helpers/AdminProfileDropdownHelper';
import { SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH } from '~src/userManagement/helpers/SiteMembershipGridAdminProfileHelper';

const cssPrefix = 'AdminProfileSiteManagementDialog';
const borderColor = applyColor(
  DesignTokens.color?.elevation?.border?.default?.subtle,
  '#e0e0e0',
);
const dataGridBorderColor = applyColor(
  DesignTokens.color?.elevation?.border?.default?.default,
  '#b2b2b2',
);

export namespace AdminProfileSiteManagementDialogStyles {
  export const DialogBodyStyle: React.CSSProperties = {
    paddingTop: 12,
    paddingBottom: 18,
    display: 'flex',
    flexDirection: 'column',
  };

  export const DialogContentWrapper = cssClass(
    cssPrefix,
    'dialog-content-wrapper',
    {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
      minWidth: 0,
      paddingTop: 12,
      paddingBottom: 12,
      borderBottom: `1px solid ${borderColor}`,
    },
  );

  export const SitesSection = cssClass(cssPrefix, 'sites-section', {
    marginBottom: '16px',
    width: '100%',
    padding: 0,
  });

  export const SitesContainer: React.CSSProperties = {
    maxHeight: '400px',
    overflowY: 'auto',
    width: '100%',
    boxSizing: 'border-box',
    padding: 0,
  };

  const rowPadding = `${TabStyles.Spacing.SmallSpace}px ${TabStyles.Spacing.MediumSpace}px`;

  export const SitesTableHeader: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: TabStyles.Spacing.MediumSpace,
    padding: rowPadding,
    borderBottom: `1px solid ${borderColor}`,
  };

  export const SiteHeaderLabel = cssClass(cssPrefix, 'site-header-label', {
    $nest: {
      '& label': { fontWeight: 600 },
    },
  });

  export const AssignedUsersHeader: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '13px',
  };

  export const SitesTableRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: TabStyles.Spacing.MediumSpace,
    padding: rowPadding,
  };

  export const SitesTableRowLast: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: TabStyles.Spacing.MediumSpace,
    padding: rowPadding,
  };

  export const SiteCell: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: TabStyles.Spacing.SmallSpace,
    minWidth: 0,
  };

  export const AssignedUsersCell: React.CSSProperties = {
    flexShrink: 0,
  };

  export const warningRowBackground: React.CSSProperties = {
    backgroundColor:
      (DesignTokens as any).color?.feedback?.default?.warning_light ??
      '#fff5e6',
  };

  const errorColor =
    (DesignTokens as any).color?.feedback?.default?.error_dark ?? '#c33';

  export const WarningBadge = cssClass(cssPrefix, 'warning-badge', {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    borderRadius: '50%',
    backgroundColor:
      (DesignTokens as any).color?.feedback?.default?.error_light ?? '#ffebee',
    color: errorColor,
    fontWeight: 600,
    fontSize: '12px',
  });

  export const WarningSection = cssClass(cssPrefix, 'warning-section', {
    marginTop: '12px',
    marginBottom: '12px',
  });

  export const LoadingSpinnerContainer: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    padding: 24,
  };

  export const AdminProfileColumnHeader: React.CSSProperties = {
    display: 'block',
    padding: '0 2px',
    textAlign: 'left',
    width: '100%',
  };

  /** Row sizing for admin profile options; width matches site-membership grid popup list (see SiteMembershipGridAdminProfileHelper). */
  export const AdminProfileDropdownListStyle: React.CSSProperties = {
    maxHeight: ADMIN_PROFILE_LIST_MAX_HEIGHT,
    overflow: 'auto',
    width: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
    minWidth: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
    maxWidth: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
  };

  export const DataGridContainer = cssClass(cssPrefix, 'data-grid-container', {
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden',
    marginTop: 8,
    borderBottom: `1px solid ${dataGridBorderColor}`,
    $nest: {
      '& .tb-react-dg-hrow': {
        borderBottom: `1px solid ${dataGridBorderColor}`,
      },
    },
  });

  export const SiteNameText: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}
